import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Download, Send, Mail, MessageCircle, CheckCircle, XCircle, FileText, Clock, User, MapPin, Calendar, Package, Phone, Building, Truck, IndianRupee, Printer, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { getApiErrorMessage } from '../../utils/apiErrors';

const DeliveryChallanDetailPage = () => {
  const { businessId, challanId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [challan, setChallan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid && challanId) {
      fetchChallan();
    }
  }, [bid, challanId]);

  const fetchChallan = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/delivery-challans/${challanId}`);
      setChallan(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load challan'));
    } finally {
      setLoading(false);
    }
  };

  const handleIssue = () => {
    setConfirmModal({
      title: 'Issue Delivery Challan',
      description: 'Items will be marked as dispatched. This cannot be undone.',
      confirmLabel: 'Issue Challan',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      iconBgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      icon: Truck,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.patch(`/delivery-challans/${challanId}/issue`);
          toast.success('Delivery challan issued');
          fetchChallan();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to issue'));
        }
      },
    });
  };

  const handleConvertToInvoice = () => {
    setConfirmModal({
      title: 'Convert to Sales Invoice',
      description: 'A new sales invoice will be created from this delivery challan.',
      confirmLabel: 'Convert',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      iconBgClass: 'bg-emerald-100',
      iconClass: 'text-emerald-600',
      icon: FileText,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await api.post(`/delivery-challans/${challanId}/convert-to-invoice`);
          toast.success(response.data.message || 'Converted to invoice');
          if (response.data.invoice?.id) {
            navigate(`/business/${bid}/sales/invoices/${response.data.invoice.id}`);
          }
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to convert'));
        }
      },
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await api.get(`/delivery-challans/${challanId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DeliveryChallan_${challan?.challan_number || challanId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await api.post(`/delivery-challans/${challanId}/send-email`);
      toast.success('Challan queued for email delivery');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = async () => {
    try {
      await api.post(`/delivery-challans/${challanId}/send-whatsapp`);
      toast.success('Challan queued for WhatsApp delivery');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send WhatsApp'));
    }
  };

  const handleBack = () => {
    navigate(`/business/${bid}/delivery-challans`);
  };

  const formatAddress = (address) => {
    if (!address) return null;
    if (typeof address === 'string') return address;
    // Handle address object
    const parts = [];
    if (address.line1) parts.push(address.line1);
    if (address.line2) parts.push(address.line2);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.pincode) parts.push(address.pincode);
    if (address.country) parts.push(address.country);
    return parts.join(', ');
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading challan details...</p>
        </div>
      </div>
    );
  }

  if (!challan) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-[16px] text-slate-600 font-medium">Delivery Challan not found</p>
          <button onClick={handleBack} className="mt-4 text-blue-600 hover:underline text-[14px]">
            ← Back to Delivery Challans
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = {
    DRAFT: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, label: 'Draft' },
    ISSUED: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Truck, label: 'Issued' },
    DELIVERED: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle, label: 'Delivered' },
    CONVERTED: { bg: 'bg-purple-100', text: 'text-purple-700', icon: FileText, label: 'Converted' },
    CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Cancelled' },
  };

  // Keys match DB uppercase values (SUPPLY_ON_APPROVAL, JOB_WORK, etc.)
  const challanTypeConfig = {
    'SUPPLY_ON_APPROVAL': { label: 'Supply on Approval', color: 'text-blue-600' },
    'JOB_WORK': { label: 'Job Work', color: 'text-purple-600' },
    'CONSIGNMENT': { label: 'Consignment / Stock Transfer', color: 'text-indigo-600' },
    'EXHIBITION': { label: 'Exhibition / Demo', color: 'text-pink-600' },
    'RETURNABLE': { label: 'Returnable Delivery', color: 'text-teal-600' },
    'NON_RETURNABLE': { label: 'Non-Returnable Delivery', color: 'text-orange-600' },
    'REPAIR': { label: 'Repair / Service', color: 'text-amber-600' },
    'SAMPLE': { label: 'Sample Delivery', color: 'text-green-600' },
    'OTHER': { label: 'Other', color: 'text-slate-600' },
  };

  const statusBadge = statusConfig[challan.status] || statusConfig.DRAFT;
  const StatusIcon = statusBadge.icon;
  const challanType = challanTypeConfig[challan.challan_type] || challanTypeConfig['OTHER'];

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const items = challan.items || [];
  const totalQty = items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* ════════════════════════════════════════════════════════════════════════
          PREMIUM HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back + Challan Info */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[20px] font-bold text-slate-800">{challan.challan_number}</h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusBadge.label}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-[12px] font-medium bg-slate-100 ${challanType.color}`}>
                    {challanType.label}
                  </span>
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Created on {formatDate(challan.created_at)} • {items.length} item{items.length !== 1 ? 's' : ''} • Total Qty: {totalQty}
                </p>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              {challan.status === 'DRAFT' && (
                <>
                  <button
                    onClick={() => navigate(`/business/${bid}/delivery-challans/${challanId}/edit`)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={handleIssue}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-blue-600/20 active:scale-[0.98]"
                  >
                    <Truck className="w-4 h-4" />
                    Issue Challan
                  </button>
                </>
              )}
              {(challan.status === 'ISSUED' || challan.status === 'DELIVERED') && (
                <button
                  onClick={handleConvertToInvoice}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-emerald-600/20 active:scale-[0.98]"
                >
                  <FileText className="w-4 h-4" />
                  Convert to Invoice
                </button>
              )}
              <div className="w-px h-8 bg-slate-200 mx-1"></div>
              <button
                onClick={handleDownloadPdf}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]"
                title="Download PDF"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]"
                title="Print"
              >
                <Printer className="w-4 h-4" />
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Send Email"
              >
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </button>
              <button
                onClick={handleSendWhatsApp}
                className="inline-flex items-center justify-center w-10 h-10 text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all duration-200 active:scale-[0.98]"
                title="Send WhatsApp"
              >
                <MessageCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MAIN CONTENT - Premium 3-Column Layout
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - Customer & Shipping (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Customer Card - Premium */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Customer</h2>
                </div>
                
                {challan.customer_name || challan.party_name ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-blue-700">
                          {(challan.customer_name || challan.party_name)?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{challan.customer_name || challan.party_name}</p>
                        <p className="text-[12px] text-slate-500">Customer</p>
                      </div>
                    </div>
                    
                    {(challan.customer_phone || challan.customer_mobile) && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {challan.customer_phone || challan.customer_mobile}
                      </div>
                    )}
                    
                    {challan.customer_email && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {challan.customer_email}
                      </div>
                    )}
                    
                    {challan.customer_gstin && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Building className="w-4 h-4 text-slate-400" />
                        GSTIN: {challan.customer_gstin}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-2">
                      <User className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-[13px] text-slate-500">No customer selected</p>
                  </div>
                )}
              </div>

              {/* Shipping Card - Premium */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                    <Truck className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Delivery Details</h2>
                </div>
                
                <div className="space-y-3">
                  {challan.shipping_address && (
                    <div>
                      <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Delivery Address</p>
                      <p className="text-[13px] text-slate-600 leading-relaxed">{formatAddress(challan.shipping_address)}</p>
                    </div>
                  )}
                  
                  {challan.transport_details && (
                    <div>
                      <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Transporter</p>
                      <p className="text-[14px] font-medium text-slate-700">{challan.transport_details}</p>
                    </div>
                  )}
                  
                  {challan.vehicle_number && (
                    <div>
                      <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Vehicle Number</p>
                      <p className="text-[14px] font-medium text-slate-700">{challan.vehicle_number}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Challan Info Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Challan Info</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Challan Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(challan.challan_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Challan Type</span>
                    <span className={`text-[13px] font-medium ${challanType.color}`}>{challanType.label}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Reference</span>
                    <span className="text-[13px] font-medium text-slate-700">{challan.reference_no || '—'}</span>
                  </div>
                  {challan.source_order_number && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Source Order</span>
                      <span className="text-[13px] font-medium text-blue-600">{challan.source_order_number}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* CENTER COLUMN - Items Table (6 cols) */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                {/* Items Header */}
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <Package className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Challan Items</h2>
                      <p className="text-[12px] text-slate-500">{items.length} item{items.length !== 1 ? 's' : ''} • Total Qty: {totalQty}</p>
                    </div>
                  </div>
                </div>
                
                {/* Items Table */}
                {items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">#</th>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Item Details</th>
                          <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Qty</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Rate</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Est. Value</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50/50 transition-colors duration-150">
                            <td className="px-5 py-4 text-[13px] text-slate-400 font-medium">{index + 1}</td>
                            <td className="px-5 py-4">
                              <p className="text-[14px] font-medium text-slate-800">{item.item_name}</p>
                              {item.hsn_code && (
                                <p className="text-[11px] text-slate-400 mt-0.5">HSN: {item.hsn_code}</p>
                              )}
                              {item.description && (
                                <p className="text-[12px] text-slate-500 mt-1">{item.description}</p>
                              )}
                            </td>
                            <td className="px-5 py-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 rounded-lg text-[13px] font-medium text-slate-700">
                                {item.quantity} {item.unit || 'Nos'}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-right text-[13px] text-slate-600">
                              ₹{(item.rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-5 py-4 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">
                                ₹{((item.quantity || 0) * (item.rate || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-[14px] text-slate-500">No items in this challan</p>
                  </div>
                )}

                {/* Notes Section */}
                {challan.notes && (
                  <div className="px-5 py-4 bg-amber-50 border-t border-amber-100">
                    <p className="text-[11px] text-amber-600 uppercase tracking-wide font-semibold mb-1">Notes</p>
                    <p className="text-[13px] text-amber-800">{challan.notes}</p>
                  </div>
                )}

                {/* Info Banner - Delivery Challan Notice */}
                <div className="px-5 py-3 bg-blue-50 border-t border-blue-100 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <p className="text-[12px] text-blue-700">
                    Delivery Challan is a logistics document. No tax is calculated. Convert to Invoice for billing.
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Summary (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Summary Card - Premium Teal */}
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-5 shadow-sm border border-teal-200/60">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-sm">
                    <Package className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Challan Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Total Items</span>
                    <span className="text-slate-800 font-medium">{items.length}</span>
                  </div>
                  
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Total Quantity</span>
                    <span className="text-slate-800 font-medium">{totalQty}</span>
                  </div>
                  
                  <div className="border-t border-teal-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Estimated Value</span>
                      <span className="text-[22px] font-bold text-teal-600">
                        ₹{(challan.estimated_value || items.reduce((sum, item) => sum + (item.quantity || 0) * (item.rate || 0), 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <p className="text-[11px] text-teal-600 mt-1">*Estimated, before tax</p>
                  </div>
                </div>
              </div>

              {/* Status Timeline Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Status</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${challan.status === 'DRAFT' ? 'bg-amber-500' : 'bg-emerald-500'}`}></div>
                    <div>
                      <p className="text-[13px] font-medium text-slate-700">Created</p>
                      <p className="text-[11px] text-slate-500">{formatDate(challan.created_at)}</p>
                    </div>
                  </div>
                  
                  {challan.issued_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-700">Issued</p>
                        <p className="text-[11px] text-slate-500">{formatDate(challan.issued_at)}</p>
                      </div>
                    </div>
                  )}
                  
                  {challan.delivered_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-700">Delivered</p>
                        <p className="text-[11px] text-slate-500">{formatDate(challan.delivered_at)}</p>
                      </div>
                    </div>
                  )}
                  
                  {challan.invoice_id && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-700">Converted to Invoice</p>
                        <button
                          onClick={() => navigate(`/business/${bid}/sales/invoices/${challan.invoice_id}`)}
                          className="text-[11px] text-purple-600 hover:underline"
                        >
                          View Invoice →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              {challan.status === 'DRAFT' && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                  <h2 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h2>
                  <div className="space-y-2">
                    <button
                      onClick={() => navigate(`/business/${bid}/delivery-challans/${challanId}/edit`)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors duration-200"
                    >
                      <Edit className="w-4 h-4 text-slate-600" />
                      <span className="text-[13px] font-medium text-slate-700">Edit Challan</span>
                    </button>
                    <button
                      onClick={handleIssue}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors duration-200"
                    >
                      <Truck className="w-4 h-4 text-blue-600" />
                      <span className="text-[13px] font-medium text-blue-700">Issue Challan</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconClass={confirmModal?.iconClass}
        iconBgClass={confirmModal?.iconBgClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default DeliveryChallanDetailPage;
